import React, { useState,useRef } from 'react';
import { ArrowLeft, Users, MapPin, User, Shield, AlertTriangle, Clock, ContactRound } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const LoginPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loginData, region, accountName, duration } = location.state || {};
  const formatDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
    } catch (error) {
      return dateTimeStr;
    }
  };
  
  const LongTextCell = ({ text, maxLength = 30 }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef(null);
    const cellRef = useRef(null);
    
    if (!text || text.length <= maxLength) {
      return <span>{text}</span>;
    }
    
    const handleMouseEnter = (e) => {
      setShowTooltip(true);
      setTimeout(() => {
        if (cellRef.current && tooltipRef.current) {
          const cellRect = cellRef.current.getBoundingClientRect();
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - cellRect.bottom;
          const tooltipHeight = tooltipRect.height;
          
          if (spaceBelow < tooltipHeight) {
            setTooltipPosition({
              top: -tooltipHeight - 5,
              left: 0
            });
          } else {
            setTooltipPosition({
              top: "100%",
              left: 0
            });
          }
        }
      }, 0);
    };
  
    return (
      <div className="relative inline-block">
        <div
          ref={cellRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setShowTooltip(false)}
          className="cursor-pointer border-b border-dotted border-gray-400"
        >
          {text.substring(0, maxLength)}...
        </div>
        {showTooltip && (
          <div 
            ref={tooltipRef}
            className="absolute z-50 w-64 p-2 text-sm bg-gray-900 text-white rounded shadow-lg"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              maxWidth: "250px",
              wordBreak: "break-word"
            }}
          >
            {text}
          </div>
        )}
      </div>
    );
  };

  const summary = loginData?.summary || {};
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-[#003366]">Login Activity Details</h1>
        </div>
        <div className="text-sm text-gray-500">
          Last {duration}
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-[#003366] mb-4">Activity Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Total Logins</div>
            <div className="text-xl font-semibold text-[#003366]">
              {summary.total_logins}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Failed Logins</div>
            <div className="text-xl font-semibold text-[#003366]">
              {summary.failed_login_count}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Region</div>
            <div className="text-xl font-semibold text-[#003366]">
              {region}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-500">Account</div>
            <div className="text-xl font-semibold text-[#003366]">
              {accountName}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <ContactRound className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">User Statistics</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Unique Users</span>
              <span className="font-semibold">{summary.unique_users}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Root Logins</span>
              <span className="font-semibold">{summary.root_login_count}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Most Active User</span>
              <span className="font-semibold">{summary.most_active_user}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Authentication Analysis</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Console Logins</span>
              <span className="font-semibold">{summary.console_login_count}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">SAML Logins</span>
              <span className="font-semibold">{summary.api_login_count}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Success Rate</span>
              <span className="font-semibold">
                {100 - summary.failed_login_percentage}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="h-6 w-6 text-[#003366]" />
            <h3 className="text-lg font-semibold text-[#003366]">Network Analysis</h3>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Unique IPs</span>
              <span className="font-semibold">{summary.unique_ip_count}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Users with Multiple IPs</span>
              <span className="font-semibold">{summary.users_with_multiple_ips}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Most Used IP</span>
              <LongTextCell text={summary.most_frequent_ip} maxLength={15} />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-[#003366] mb-4">Login Details</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auth Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MFA Used
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {  loginData?.data && loginData.data.length > 0 ? ( loginData?.data?.map((login, index) => (
                  <tr key={index} className={`hover:bg-gray-50 ${
                    login.Status?.toLowerCase() === 'failure' ? 'bg-red-50' : ''
                  }`}>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <LongTextCell text={login.username} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {login.ip_address}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {login.AuthType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center space-x-1">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span>{formatDateTime(login.eventtime)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        login.MFAUsed === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {login.MFAUsed}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        login.Status?.toLowerCase() === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {login.Status}
                      </span>
                    </td>
                  </tr>
                ))):(

                  <tr>
              
                    <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
              
                      <div className="flex flex-col items-center justify-center">
              
                        <AlertTriangle className="h-10 w-10 text-gray-400 mb-2" />
              
                        <p className="text-lg font-medium">No data to display</p>
              
                        <p className="text-sm">No Login events were found for the selected time period and filters.</p>
              
                      </div>
              
                    </td>
              
                  </tr>
              
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;